// Catalog-specific API methods
import api, { USE_REAL_API, updateLoadingState, fetchData } from './api';
import { API_URL } from './config';

import productCandles from "../assets/images/products/candle-main.png";
import productDiffuse from "../assets/images/products/diffuse-main.png";
import productSashe from "../assets/images/products/sashe-main.png";
import productMelts from "../assets/images/products/melts-main.png";
import productAuto from "../assets/images/products/auto-main.png";
import productMasage from "../assets/images/products/massage-main.png";
import productLanch from "../assets/images/products/lanch-candle-main.png";
import productDecorate from "../assets/images/products/decorate-main.jpg";

// API endpoints for catalog operations
const ENDPOINTS = {
  categories: '/categories',
  catalog: '/products',
  aromas: '/products/aromas',
  waxes: '/products/waxes',
  colors: '/products/colors',
  holders: '/products/holders',
  // PRODUCTION: Use this endpoint when API is ready
  productsByCategory: (categoryId?: string) => {
    // "collection" и пустой идентификатор означают общий список продуктов (для коллекций или спец. фильтров)
    if (!categoryId || categoryId === 'collection' || categoryId === 'all') {
      return '/products';
    }
    return `/products?category=${categoryId}`;
  },
  productDetail: (productId) => `/products/${productId}`,
};

// Category shape returned by the backend
export interface Category {
  key: string;
  name: string;
  image?: string;
  description?: string;
  slug?: string;
  products_count?: number;
}

export interface ProductSearchMatchInfo {
  name?: boolean;
  article?: boolean;
  aroma?: string[];
}

export interface ProductSearchItem {
  id: number;
  name: string;
  slug?: string;
  image?: string;
  category?: { key?: string; name?: string } | null;
  article?: string | null;
  matches?: ProductSearchMatchInfo;
}

export interface ProductSearchResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProductSearchItem[];
  facets?: {
    categories?: Array<{ key: string; name: string; count: number }>;
    aromas?: Array<{ id: number; name: string; count: number }>;
  };
}

const resolveProductImage = (prod: any): string => {
  if (!prod) return '';
  if (typeof prod.image === 'string' && prod.image) return prod.image;
  if (Array.isArray(prod.images) && prod.images.length) {
    const main = prod.images.find((img: any) => img?.is_main || img?.is_main === 1) || prod.images[0];
    if (typeof main === 'string') return main;
    if (main && typeof main.url === 'string') return main.url;
  }
  if (typeof prod.main_image === 'string') return prod.main_image;
  if (typeof prod.preview_image === 'string') return prod.preview_image;
  return '';
};

/**
 * Fetch all product categories
 * 
 * PRODUCTION: Remove mock data section when API is fully implemented
 */
export const fetchCategories = async (): Promise<Category[]> => {
  updateLoadingState('categories', true);
  try {
    const data = await fetchData(ENDPOINTS.categories);
    // debug info to help investigate why mocks are returned
    if (typeof window !== 'undefined') {
      console.debug('[fetchCategories] USE_REAL_API=', USE_REAL_API, 'API_URL=', API_URL, 'rawResponse=', data);
    } else {
      console.log('[fetchCategories] server-side USE_REAL_API=', USE_REAL_API);
    }
    // Normalize different possible response shapes from backend
    // - direct array: [ { ... }, ... ]
    // - wrapped in { categories: [...] }
    // - wrapped in { data: [...] } (common pattern)
    if (Array.isArray(data)) {
      return data as Category[];
    }
    if (data && Array.isArray((data as any).categories)) {
      return (data as any).categories as Category[];
    }
    if (data && Array.isArray((data as any).data)) {
      return (data as any).data as Category[];
    }

    // Try to locate first array anywhere in the response (helpful for wrapped responses)
    const findArrayInObject = (obj: any, depth = 0): any[] | null => {
      if (depth > 2 || obj == null) return null;
      if (Array.isArray(obj)) return obj;
      if (typeof obj === 'object') {
        for (const key of Object.keys(obj)) {
          const val = (obj as any)[key];
          if (Array.isArray(val)) return val;
        }
        // try one level deeper
        for (const key of Object.keys(obj)) {
          const val = (obj as any)[key];
          const res = findArrayInObject(val, depth + 1);
          if (res) return res;
        }
      }
      return null;
    };

    const found = findArrayInObject(data);
    if (found) {
      return found as Category[];
    }

    console.warn('fetchCategories: unexpected response shape, returning empty list', data);
    return [];
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    // PRODUCTION: Consider if you want to fallback to mock data on error
    // or if you want to propagate the error to the UI
    return [];
  } finally {
    updateLoadingState('categories', false);
  }
};

/**
 * Fetch all products (general catalog)
 * 
 * PRODUCTION: Remove mock data section when API is fully implemented
 */
export const fetchCatalog = async () => {
  updateLoadingState('catalog', true);
  try {
    const data = await fetchData(ENDPOINTS.catalog);

    // Possible shapes:
    // - object mapping: { candles: [...], diffusers: [...] }
    // - flat array of products: [ { id, category, ... }, ... ]
    // - wrapped: { data: [...] } or { products: [...] }
    if (!data) {
      // No data returned — return empty grouped catalog
      return {};
    }

    // If already an object mapping categories to arrays, return as-is
    if (typeof data === 'object' && !Array.isArray(data)) {
      const allValuesAreArrays = Object.values(data).every(v => Array.isArray(v));
      if (allValuesAreArrays) {
        return data;
      }
    }

    // If it's an array (flat list) or wrapped array, group by category key
    const productsArray = Array.isArray(data)
      ? data
      : (Array.isArray((data as any).data) ? (data as any).data : (Array.isArray((data as any).products) ? (data as any).products : null));

    if (productsArray && Array.isArray(productsArray)) {
      // Group by category key if available, otherwise put under 'all'
      const grouped = productsArray.reduce((acc, product: any) => {
        const key = product.category_key || product.category || 'all';
        if (!acc[key]) acc[key] = [];
        acc[key].push(product);
        return acc;
      }, {} as Record<string, any[]>);
      return grouped;
    }

    console.warn('fetchCatalog: unexpected response shape, returning empty catalog', data);
    return {};
  } catch (error) {
    console.error('Failed to fetch catalog:', error);
    // PRODUCTION: Consider if you want to fallback to mock data on error
    return {};
  } finally {
    updateLoadingState('catalog', false);
  }
};

/**
 * Fetch products by category
 * 
 * @param {string} categoryId - The category identifier
 * PRODUCTION: Remove mock data section when API is fully implemented
 */
export const fetchProductsByCategory = async (categoryId, options = {}) => {
  // options: { page, page_size, url, filters }
  const page = (options as any).page || 1;
  const page_size = (options as any).page_size || 20;
  const url = (options as any).url || null;
  const filters = (options as any).filters || {};

  // Helper to normalize product items for the category list
  const normalizeProducts = (arr: any[]) => (arr || []).map((prod: any) => {
    const image = resolveProductImage(prod);

    // Normalize category to string for list display
    const category = typeof prod.category === 'string' ? prod.category : (prod.category && (prod.category.name || prod.category.key)) || '';

    // Normalize price to number if needed
    const price = typeof prod.price === 'string' ? parseFloat(prod.price) : (prod.price || 0);

    return {
      ...prod,
      image,
      category,
      price,
    };
  });

  // Note: mock mode removed — always call real API

  // Real API: request paginated endpoint
  updateLoadingState('catalog', true);
  try {
    // If absolute/relative next URL provided in options, fetch it directly
    let raw;
    if (url) {
      raw = await fetchData(url);
    } else {
      // Build query params including supported filters so frontend can ask backend to filter
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(page_size));
      // supported filters: price_min, price_max, in_stock, q, aroma, wax, color, holder, new_year
      if (filters.price_min != null) params.set('price_min', String(filters.price_min));
      if (filters.price_max != null) params.set('price_max', String(filters.price_max));
      if (filters.in_stock != null) params.set('in_stock', String(filters.in_stock));
      if (filters.q) params.set('q', String(filters.q));
      if (filters.aroma) params.set('aroma', String(filters.aroma));
      if (filters.wax) params.set('wax', String(filters.wax));
      if (filters.color) params.set('color', String(filters.color));
      if (filters.holder) params.set('holder', String(filters.holder));
      // new_year comes from collection toggle; backend expects boolean flag new_year=true
      if (filters.collection === 'new_year') params.set('new_year', 'true');

      const base = ENDPOINTS.productsByCategory(categoryId);
      const sep = base.includes('?') ? '&' : '?';
      const urlToFetch = `${base}${sep}${params.toString()}`;
      raw = await fetchData(urlToFetch);
    }

    // Standard DRF paginated shape: { count, next, previous, results }
    if (raw && Array.isArray((raw as any).results)) {
      const results = normalizeProducts((raw as any).results);
      return { results, count: (raw as any).count || results.length, next: (raw as any).next || null, previous: (raw as any).previous || null };
    }

    // Other wrapped shapes
    if (Array.isArray(raw)) {
      const results = normalizeProducts(raw);
      return { results, count: results.length, next: null, previous: null };
    }
    if (raw && Array.isArray((raw as any).data)) {
      const results = normalizeProducts((raw as any).data);
      return { results, count: (raw as any).count || results.length, next: (raw as any).next || null, previous: (raw as any).previous || null };
    }

    // Try to find array in object
    const findArrayInObject = (obj: any, depth = 0): any[] | null => {
      if (depth > 2 || obj == null) return null;
      if (Array.isArray(obj)) return obj;
      if (typeof obj === 'object') {
        for (const key of Object.keys(obj)) {
          const val = (obj as any)[key];
          if (Array.isArray(val)) return val;
        }
        for (const key of Object.keys(obj)) {
          const val = (obj as any)[key];
          const res = findArrayInObject(val, depth + 1);
          if (res) return res;
        }
      }
      return null;
    };

    const arr = findArrayInObject(raw) || [];
    const results = normalizeProducts(arr as any[]);
    return { results, count: (raw && (raw.count || results.length)) || results.length, next: (raw && raw.next) || null, previous: (raw && raw.previous) || null };
  } catch (error) {
    console.error(`Failed to fetch products for category ${categoryId}:`, error);
    // PRODUCTION: Consider if you want to fallback to mock data on error
    return { results: [], count: 0, next: null, previous: null };
  } finally {
    updateLoadingState('catalog', false);
  }
};

interface ProductSearchParams {
  q: string;
  limit?: number;
  offset?: number;
  includeFacets?: boolean;
  category?: string;
  aroma?: string | number;
}

export const fetchProductSearch = async ({ q, limit = 10, offset = 0, includeFacets = false, category, aroma }: ProductSearchParams): Promise<ProductSearchResponse> => {
  const trimmed = (q || '').trim();
  if (!trimmed) {
    throw new Error('Search query is required');
  }

  const params = new URLSearchParams();
  params.set('q', trimmed);
  params.set('limit', String(Math.min(Math.max(limit, 1), 50)));
  params.set('offset', String(Math.max(offset, 0)));
  if (includeFacets) params.set('include_facets', 'true');
  if (category) params.set('category', category);
  if (aroma != null && aroma !== '') params.set('aroma', String(aroma));

  const response = await fetchData(`/products/search/?${params.toString()}`);
  const normalizeMatches = (matches: any): ProductSearchMatchInfo => ({
    name: !!matches?.name,
    article: !!matches?.article,
    aroma: Array.isArray(matches?.aroma) ? matches.aroma.filter(Boolean) : []
  });

  const results: ProductSearchItem[] = Array.isArray(response?.results)
    ? response.results.map((item: any) => ({
        id: item.id,
        name: item.name || item.title || '',
        slug: item.slug,
        image: item.image || resolveProductImage(item) || '',
        category: item.category || null,
        article: item.article || item.sku || null,
        matches: normalizeMatches(item.matches)
      }))
    : [];

  const facets = response?.facets || {};

  return {
    count: typeof response?.count === 'number' ? response.count : results.length,
    next: response?.next || null,
    previous: response?.previous || null,
    results,
    facets: {
      categories: Array.isArray(facets.categories) ? facets.categories : [],
      aromas: Array.isArray(facets.aromas) ? facets.aromas : []
    }
  };
};

/**
 * Fetch product details by ID
 * 
 * @param {number|string} productId - The product identifier
 * PRODUCTION: Remove mock data section when API is fully implemented
 */
export const fetchProductDetail = async (productId) => {
  // Always use real API; removed mock-only branch
  updateLoadingState('product', true);
  try {
    // productId may be numeric id or slug string. Backend supports both:
    // - /products/<id>/
    // - /products/slug/<slug>/  (preferred)
    let data;
    const asString = String(productId || '');
    const isNumericId = /^\d+$/.test(asString);

    if (isNumericId) {
      data = await fetchData(ENDPOINTS.productDetail(productId));
    } else {
      // Try slug endpoint first
      try {
        const slugUrl = `/products/slug/${encodeURIComponent(asString)}`;
        data = await fetchData(slugUrl);
      } catch (slugErr) {
        // Fallback: try query by slug on list endpoint and then fetch by id
        try {
          const list = await fetchData(`${ENDPOINTS.catalog}?slug=${encodeURIComponent(asString)}`);
          const results = Array.isArray(list?.results) ? list.results : (Array.isArray(list) ? list : (Array.isArray(list?.data) ? list.data : []));
            // Prefer an exact slug match. Some backends treat `?slug=` as a search and
            // may return unrelated items; pick only an exact match to avoid accidental
            // redirection to a random product.
            const exact = (results || []).find((r: any) => {
              const s = r && (r.slug || r.slug === 0 ? String(r.slug) : undefined);
              return s === asString || (s && decodeURIComponent(asString) === s);
            });
            if (exact && exact.id) {
              data = await fetchData(ENDPOINTS.productDetail(exact.id));
            } else {
              // No exact match: treat as not found
              const notFoundErr: any = new Error('Product not found');
              notFoundErr.response = { status: 404 };
              throw notFoundErr;
            }
        } catch (qErr) {
          throw qErr;
        }
      }
    }
    // Normalize product detail shapes: { product: {...} } or { data: {...} } or direct object
    if (!data) throw new Error('Empty product detail response');
  const raw = (data as any).product || (data as any).data || data;

    // Build characteristics array expected by UI (support new backend shape)
    const characteristics: Array<{ name: string; value: string }> = [];
    // basic numeric/text fields
    if (raw.weight != null) characteristics.push({ name: 'Вес', value: `${raw.weight}` });
    if (raw.volume != null) characteristics.push({ name: 'Объем', value: `${raw.volume}` });
    if (raw.burn_time != null) characteristics.push({ name: 'Время горения', value: `${raw.burn_time}` });
    if (raw.article) characteristics.push({ name: 'Артикул', value: `${raw.article}` });

    // structured objects: wax, wick, holder, cap
    if (raw.wax && raw.wax.name) characteristics.push({ name: 'Материал', value: raw.wax.name });
    if (raw.wick && raw.wick.name) characteristics.push({ name: 'Фитиль', value: raw.wick.name });
    if (raw.holder && raw.holder.name) characteristics.push({ name: 'Стакан', value: raw.holder.name });
    if (raw.cap && raw.cap.name) characteristics.push({ name: 'Крышка', value: raw.cap.name });

    // Normalize notes - backend may provide `aroma` object with arrays
    const notes = {
      top: Array.isArray(raw.aroma?.top) ? raw.aroma.top : (Array.isArray(raw.top_notes) ? raw.top_notes : []),
      middle: Array.isArray(raw.aroma?.middle) ? raw.aroma.middle : (Array.isArray(raw.middle_notes) ? raw.middle_notes : []),
      base: Array.isArray(raw.aroma?.base) ? raw.aroma.base : (Array.isArray(raw.base_notes) ? raw.base_notes : []),
    };

    // Normalize images to simple array of urls
    let images: string[] = [];
    if (Array.isArray(raw.images)) {
      images = raw.images.map((img: any) => img.url || img);
    } else if (Array.isArray((raw as any).images_urls)) {
      images = (raw as any).images_urls;
    } else if (raw.main_image) {
      images = [raw.main_image];
    }

    // Normalize price and scent
    const price = typeof raw.price === 'string' ? parseFloat(raw.price) : (raw.price || 0);
    const scent = raw.aroma?.name || raw.scent || raw.name || '';

    // Build normalized product object matching mockProductDetailData shape where possible
    const normalized = {
      ...raw,
      // Normalize category to a string (backend may return object {key,name,image})
      category: typeof raw.category === 'string' ? raw.category : (raw.category && (raw.category.name || raw.category.key)) || '',
      // friendly fields expected by UI
      price,
      scent,
      characteristics,
      notes,
      images,
      // Ensure relatedProducts is always an array of simplified objects
      relatedProducts: (Array.isArray(raw.relatedProducts)
        ? raw.relatedProducts
        : (Array.isArray(raw.related_products) ? raw.related_products : [])
      ).map((rp: any) => ({
        id: rp.id,
        name: rp.name || rp.title || '',
        category: typeof rp.category === 'string' ? rp.category : (rp.category && (rp.category.name || rp.category.key)) || '',
        price: rp.price || rp.cost || 0,
        image: (rp.image && (typeof rp.image === 'string' ? rp.image : rp.image.url)) || rp.main_image || ''
      })),
    };

    return normalized;
  } catch (error) {
    console.error(`Failed to fetch product details for ID ${productId}:`, error);
    // Propagate error to caller (UI hook will handle it)
    throw error;
  } finally {
    updateLoadingState('product', false);
  }
};

/**
 * Fetch list of available aromas from backend
 * Endpoint: /products/aromas (may return array of strings or array of objects {id, name} or wrapped shapes)
 */
export const fetchAromas = async (options: { page?: number; page_size?: number; url?: string; search?: string } = {}): Promise<{ results: Array<{ id?: number; name: string }>; count: number; next: string | null; previous: string | null }> => {
  // Supports paginated DRF responses: { count, next, previous, results }
  const page = options.page || 1;
  const page_size = options.page_size || 20;
  const url = options.url || null;

  updateLoadingState('aromas', true);
  try {
    let raw;
    if (url) {
      raw = await fetchData(url);
    } else {
      // If a search string is provided, some backends may only support searching aromas via the products list
      // (e.g. /products/?aroma=<query> does icontains on product.aroma). In that case we fetch products and
      // derive unique aroma names from returned products so we can surface matching aromas even if they're
      // not included on the first page of the /products/aromas endpoint.
      if (options.search != null && String(options.search).trim() !== '') {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('page_size', String(page_size));
        params.set('aroma', String(options.search).trim());
        const base = ENDPOINTS.catalog; // /products
        const sep = base.includes('?') ? '&' : '?';
        const urlToFetch = `${base}${sep}${params.toString()}`;
        raw = await fetchData(urlToFetch);
      } else {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('page_size', String(page_size));
        const base = ENDPOINTS.aromas;
        const sep = base.includes('?') ? '&' : '?';
        const urlToFetch = `${base}${sep}${params.toString()}`;
        raw = await fetchData(urlToFetch);
      }
    }

    if (!raw) {
      return { results: [], count: 0, next: null, previous: null };
    }

    // If we fetched products (because search was provided), derive unique aroma names from products
    const productsArray = Array.isArray((raw as any).results) ? (raw as any).results : (Array.isArray(raw) ? raw : (Array.isArray((raw as any).data) ? (raw as any).data : []));

    // Helper to extract aroma name from a product object
    const extractAromaName = (prod: any) => {
      if (!prod) return null;
      if (prod.aroma && typeof prod.aroma === 'string') return prod.aroma;
      if (prod.aroma && typeof prod.aroma === 'object' && prod.aroma.name) return prod.aroma.name;
      if (typeof prod.scent === 'string' && prod.scent) return prod.scent;
      // fallback to any field that looks like a name
      return prod.name || null;
    };

    if (options.search != null && String(options.search).trim() !== '') {
      const seen = new Set<string>();
      const derived: Array<{ name: string }> = [];
      (productsArray || []).forEach((p: any) => {
        const n = extractAromaName(p);
        if (n && !seen.has(n)) {
          seen.add(n);
          derived.push({ name: n });
        }
      });
      const count = (raw && (raw as any).count) || derived.length;
      const next = raw && (raw as any).next ? (raw as any).next : null;
      const previous = raw && (raw as any).previous ? (raw as any).previous : null;
      return { results: derived, count, next, previous };
    }

    // prefer DRF shape for aromas endpoint
    let arr: any[] | null = null;
    if (Array.isArray((raw as any).results)) arr = (raw as any).results;
    else if (Array.isArray(raw)) arr = raw;
    else if (Array.isArray((raw as any).data)) arr = (raw as any).data;
    else {
      // find first array in object
      const findArray = (obj: any, depth = 0): any[] | null => {
        if (!obj || depth > 2) return null;
        if (Array.isArray(obj)) return obj;
        if (typeof obj === 'object') {
          for (const k of Object.keys(obj)) {
            const r = findArray(obj[k], depth + 1);
            if (r) return r;
          }
        }
        return null;
      };
      arr = findArray(raw) || [];
    }

    const normalized = (arr as any[]).map(item => {
      if (!item) return null;
      if (typeof item === 'string') return { name: item };
      if (typeof item === 'object') return { id: item.id, name: item.name || item.title || item.slug || String(item) };
      return null;
    }).filter(Boolean) as Array<{ id?: number; name: string }>;

    const count = (raw && ((raw as any).count || normalized.length)) || normalized.length;
    const next = raw && (raw as any).next ? (raw as any).next : null;
    const previous = raw && (raw as any).previous ? (raw as any).previous : null;

    return { results: normalized, count, next, previous };
  } catch (err) {
    console.error('Failed to fetch aromas:', err);
    return { results: [], count: 0, next: null, previous: null };
  } finally {
    updateLoadingState('aromas', false);
  }
};

/**
 * Generic helper for fetching simple dictionary lists (waxes, colors, holders)
 * Endpoint is expected to return array of strings or objects with `id`/`name` or DRF paginated shape.
 */
const fetchDictionary = async (options: { endpoint: string; loadingKey: string; page?: number; page_size?: number; url?: string }): Promise<{ results: Array<{ id?: number; name: string }>; count: number; next: string | null; previous: string | null }> => {
  const page = options.page || 1;
  const page_size = options.page_size || 100;
  const url = options.url || null;

  updateLoadingState(options.loadingKey, true);
  try {
    let raw;
    if (url) {
      raw = await fetchData(url);
    } else {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(page_size));
      const base = options.endpoint;
      const sep = base.includes('?') ? '&' : '?';
      const urlToFetch = `${base}${sep}${params.toString()}`;
      raw = await fetchData(urlToFetch);
    }

    if (!raw) {
      return { results: [], count: 0, next: null, previous: null };
    }

    let arr: any[] | null = null;
    if (Array.isArray((raw as any).results)) arr = (raw as any).results;
    else if (Array.isArray(raw)) arr = raw;
    else if (Array.isArray((raw as any).data)) arr = (raw as any).data;
    else {
      const findArray = (obj: any, depth = 0): any[] | null => {
        if (!obj || depth > 2) return null;
        if (Array.isArray(obj)) return obj;
        if (typeof obj === 'object') {
          for (const k of Object.keys(obj)) {
            const r = findArray(obj[k], depth + 1);
            if (r) return r;
          }
        }
        return null;
      };
      arr = findArray(raw) || [];
    }

    const normalized = (arr as any[]).map(item => {
      if (!item) return null;
      if (typeof item === 'string') return { name: item };
      if (typeof item === 'object') return { id: item.id, name: item.name || item.title || item.slug || String(item) };
      return null;
    }).filter(Boolean) as Array<{ id?: number; name: string }>;

    const count = (raw && ((raw as any).count || normalized.length)) || normalized.length;
    const next = raw && (raw as any).next ? (raw as any).next : null;
    const previous = raw && (raw as any).previous ? (raw as any).previous : null;

    return { results: normalized, count, next, previous };
  } catch (err) {
    console.error(`Failed to fetch dictionary for ${options.endpoint}:`, err);
    return { results: [], count: 0, next: null, previous: null };
  } finally {
    updateLoadingState(options.loadingKey, false);
  }
};

export const fetchWaxes = (options: { page?: number; page_size?: number; url?: string } = {}) =>
  fetchDictionary({ endpoint: ENDPOINTS.waxes, loadingKey: 'waxes', ...options });

export const fetchColors = (options: { page?: number; page_size?: number; url?: string } = {}) =>
  fetchDictionary({ endpoint: ENDPOINTS.colors, loadingKey: 'colors', ...options });

export const fetchHolders = (options: { page?: number; page_size?: number; url?: string; type?: string } = {}) =>
  fetchDictionary({ endpoint: options.type ? `${ENDPOINTS.holders}?type=${encodeURIComponent(options.type)}` : ENDPOINTS.holders, loadingKey: 'holders', ...options });

